'use client'

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { User } from '@supabase/supabase-js'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { Database } from '@/integrations/supabase/types'

type UserProfile = Database['public']['Tables']['users']['Row'] & {
  facility_voice_enabled?: boolean | null
  facility_type?: string | null
  facility_org_type?: string | null
  facility_branding?: {
    display_name?: string | null
    logo_url?: string | null
    primary_color?: string | null
    secondary_color?: string | null
    org_type?: string | null
  } | null
  is_affiliate?: boolean | null
  affiliate_status?: string | null
  linked_facilities?: any[] | null
  hide_demo_child?: boolean | null
  scholar_pass_id?: string | null
  nfp_pass_id?: string | null
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  isLoading: boolean
  isFetchingProfile: boolean
  signIn: (email: string, password: string) => Promise<{ data: unknown; error: unknown }>
  signUp: (email: string, password: string, name?: string) => Promise<{ data: unknown; error: unknown }>
  signInWithGoogle: () => Promise<{ data: unknown; error: unknown }>
  signInWithFacebook: () => Promise<{ data: unknown; error: unknown }>
  signOut: () => Promise<{ error: unknown }>
  updateProfile: (updates: Partial<Database['public']['Tables']['users']['Update']>) => Promise<{ data: unknown; error: unknown }>
  resetPassword: (email: string) => Promise<{ data: unknown; error: unknown }>
  refreshUserProfile: () => Promise<void>
  switchWorkspace: (facilityId: string, role: string) => Promise<{ error: unknown }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Global cache for instant auth state (synchronous access to cached session)
const AUTH_CACHE_KEY = 'sage_auth_cache'
let cachedAuthState: { user: User | null; timestamp: number } | null = null

// Helper to read cached auth synchronously from memory
function getCachedAuth(): { user: User | null; isExpired: boolean } {
  if (cachedAuthState) {
    const age = Date.now() - cachedAuthState.timestamp
    return {
      user: cachedAuthState.user,
      isExpired: age > 30000 // Cache expires after 30 seconds
    }
  }

  // Try reading from localStorage as fallback
  try {
    const stored = localStorage.getItem(AUTH_CACHE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      const age = Date.now() - parsed.timestamp
      if (age < 30000) {
        cachedAuthState = parsed
        return { user: parsed.user, isExpired: false }
      }
    }
  } catch {
    // Ignore parse errors
  }

  return { user: null, isExpired: true }
}

// Helper to cache auth state
function cacheAuth(user: User | null) {
  cachedAuthState = { user, timestamp: Date.now() }
  try {
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cachedAuthState))
  } catch {
    // Ignore storage errors
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    // INSTANT: Read from cache synchronously on mount
    const cached = getCachedAuth()
    if (!cached.isExpired) {
      console.log('[AUTH CONTEXT] 🚀 Instant auth from cache:', cached.user ? cached.user.id : 'explicitly logged out')
      return cached.user || null
    }
    return null
  })

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(() => {
    // If we have cached user, we're not loading (unless it's expired OR explicitly null so we forces the first load to double-check)
    const cached = getCachedAuth()
    return cached.isExpired || cached.user === null
  })
  const [isFetchingProfile, setIsFetchingProfile] = useState(false)

  const supabase = createClientComponentClient()

  // Normalize DB row → strict UserProfile shape with safe defaults
  const toUserProfile = useCallback((row: any): UserProfile => {
    return {
      id: row.id,
      email: row.email,
      name: row.name ?? null,
      age: row.age ?? null,
      age_bracket: row.age_bracket ?? null,
      gender: row.gender ?? null,
      preferred_persona: row.preferred_persona ?? null,
      country_code: row.country_code ?? null,
      subscription_status: (row.subscription_status ?? 'free'),
      email_verified: row.email_verified ?? null,
      facility_id: row.facility_id ?? null,
      facility_role: row.facility_role ?? null,
      subscription_plan_id: row.subscription_plan_id ?? null,
      subscription_start_date: row.subscription_start_date ?? null,
      subscription_end_date: row.subscription_end_date ?? null,
      trial_end_date: row.trial_end_date ?? null,
      free_questions_used: (row.free_questions_used ?? 0) as number,
      preferences: (row.preferences || {}) as unknown as UserProfile['preferences'],
      is_onboarded: (row.is_onboarded ?? false) as boolean,
      created_at: row.created_at ?? new Date().toISOString(),
      updated_at: row.updated_at ?? new Date().toISOString(),
      last_voice_conversation_at: row.last_voice_conversation_at ?? null,
      voice_conversations_count: row.voice_conversations_count ?? null,
      voice_tokens_used: row.voice_tokens_used ?? null,
      voice_tokens_used_this_month: row.voice_tokens_used_this_month ?? null,
      voice_usage_month_start: row.voice_usage_month_start ?? null,
      facility_voice_enabled: row.facilities?.voice_enabled ?? null,
      facility_type: row.facilities?.facility_type ?? null,
      facility_org_type: (row.facilities as any)?.branding?.org_type ?? null,
      facility_branding: (row.facilities as any)?.branding
        ? {
            display_name: (row.facilities as any).branding.display_name ?? null,
            logo_url: (row.facilities as any).branding.logo_url ?? null,
            primary_color: (row.facilities as any).branding.primary_color ?? null,
            secondary_color: (row.facilities as any).branding.secondary_color ?? null,
            org_type: (row.facilities as any).branding.org_type ?? null,
          }
        : null,
      active_plan: row.active_plan ?? null,
      global_academic_profile: row.global_academic_profile ?? null,
      hide_grades: row.hide_grades ?? null,
      plan_expires_at: row.plan_expires_at ?? null,
      prep_foundation_access: row.prep_foundation_access ?? null,
      prep_pathway_mode: row.prep_pathway_mode ?? null,
      prep_pathway_set_at: row.prep_pathway_set_at ?? null,
      prep_pathway_set_by: row.prep_pathway_set_by ?? null,
      stripe_customer_id: row.stripe_customer_id ?? null,
      university_access: row.university_access ?? null,
      sponsor_id: row.sponsor_id ?? null,
      is_affiliate: row.is_affiliate ?? false,
      affiliate_status: row.affiliate_status ?? null,
      affiliate_code_used: row.affiliate_code_used ?? null,
      affiliate_discount_applied: row.affiliate_discount_applied ?? null,
      admin_override: row.admin_override ?? null,
      bible_school_access: row.bible_school_access ?? false,
      elite_academy_access: row.elite_academy_access ?? false,
      linked_facilities: row.user_facilities ?? [],
      hide_demo_child: row.hide_demo_child ?? false,
      scholar_pass_id: row.scholar_pass_id ?? null,
      nfp_pass_id: row.nfp_pass_id ?? null,
      credential_name: row.credential_name ?? null,
      credential_name_locked: row.credential_name_locked ?? false,
      identity_verified: row.identity_verified ?? false,
      profile_photo_path: row.profile_photo_path ?? null,
      profile_photo_verified: row.profile_photo_verified ?? false,
      public_slug: row.public_slug ?? null,
      verification_pin_generated_at: row.verification_pin_generated_at ?? null,
      is_active: row.is_active ?? true,
      password_set_at: row.password_set_at ?? null,
    } as unknown as UserProfile
  }, [])

  // Throttle profile fetches globally
  const profileFetchCache = React.useRef<{
    userId: string | null
    lastFetchAt: number
    inFlight: boolean
  }>({
    userId: null,
    lastFetchAt: 0,
    inFlight: false
  })

  const authAbortControllerRef = React.useRef<AbortController | null>(null)

  const fetchUserProfile = useCallback(async (userId: string, userEmail?: string, authUserFromSession?: User | null, retryAttempt = 0) => {
    if (!userId) return

    // Diagnostic log for profile fetch
    console.log('[AUTH CONTEXT] fetchUserProfile start:', { userId, hasProvidedUser: !!authUserFromSession, retryAttempt })
    const cache = profileFetchCache.current

    // NEW — before the existing throttle check
    if (cache.inFlight && cache.userId === userId && retryAttempt === 0) {
      console.log('[AUTH CONTEXT] Profile fetch already in-flight for this user — skipping duplicate')
      return
    }

    // Only abort previous fetch if it's for a DIFFERENT user
    if (authAbortControllerRef.current && cache.userId !== userId) {
      authAbortControllerRef.current.abort()
      authAbortControllerRef.current = new AbortController()
    } else if (!authAbortControllerRef.current) {
      authAbortControllerRef.current = new AbortController()
    }

    const signal = authAbortControllerRef.current.signal

    // Prevent infinite retries
    if (retryAttempt > 3) {
      console.error('[AUTH CONTEXT] Max retry attempts reached for fetching user profile')
      return
    }

    // Throttle duplicate fetches within 15s
    const now = Date.now()
    if (
      cache.userId === userId &&
      now - cache.lastFetchAt < 15000 &&
      retryAttempt === 0
    ) {
      console.log('[AUTH CONTEXT] Profile fetch throttled (fetched recently)')
      return
    }

    if (retryAttempt === 0) {
      cache.inFlight = true
      cache.userId = userId // Set early so in-flight guard works
      setIsFetchingProfile(true)
    }

    try {
      let query = supabase
        .from('users')
        .select('*, facilities(voice_enabled, facility_type, branding), user_facilities(*, facilities(name, type))')
        .eq('id', userId)

      if (signal) {
        query = query.abortSignal(signal)
      }

      const { data, error } = await query.maybeSingle()

      if (error) {
        // Ignore abort errors as they are expected during navigation or redundant calls
        if (error.message?.includes('AbortError') || error.code === 'ABORT_ERR') {
          console.log('[AUTH CONTEXT] Profile fetch aborted')
          return
        }
        console.error('[AUTH CONTEXT] Error fetching user profile:', error)

        // If API returns 401/406 or RLS blocks, synthesize a minimal profile
        const errText = `${error.code || ''} ${error.message || ''}`.toLowerCase()
        if (errText.includes('401') || errText.includes('406') || errText.includes('row-level security')) {
          const nowIso = new Date().toISOString()
          const minimalProfile: UserProfile = {
            id: userId,
            email: userEmail || '',
            name: null,
            age: null,
            age_bracket: null,
            gender: null,
            preferred_persona: null,
            country_code: null,
            subscription_status: 'free',
            subscription_end_date: null,
            facility_id: null,
            facility_role: null,
            free_questions_used: 0,
            preferences: {} as unknown as UserProfile['preferences'],
            is_onboarded: false,
            created_at: nowIso,
            updated_at: nowIso,
            email_verified: null,
            subscription_plan_id: null,
            subscription_start_date: null,
            trial_end_date: null,
            last_voice_conversation_at: null,
            voice_conversations_count: 0,
            voice_tokens_used: 0,
            voice_tokens_used_this_month: 0,
            voice_usage_month_start: nowIso,
            facility_voice_enabled: null,
            facility_type: null,
            facility_org_type: null,
            active_plan: null,
            global_academic_profile: null,
            hide_grades: null,
            plan_expires_at: null,
            prep_foundation_access: null,
            prep_pathway_mode: 'guided',
            prep_pathway_set_at: null,
            prep_pathway_set_by: null,
            stripe_customer_id: null,
            university_access: null,
            bible_school_access: false,
            elite_academy_access: false,
            sponsor_id: null,
            is_affiliate: false,
            affiliate_status: null,
            affiliate_code_used: null,
            affiliate_discount_applied: null,
            admin_override: null,
            hide_demo_child: false,
            credential_name: null,
            credential_name_locked: false,
            identity_verified: false,
            profile_photo_path: null,
            profile_photo_verified: false,
            public_slug: null,
            verification_pin_generated_at: null,
            is_active: true,
          } as UserProfile
          setUserProfile(minimalProfile)
          profileFetchCache.current.userId = userId
          profileFetchCache.current.lastFetchAt = Date.now()
          if (retryAttempt === 0) {
            profileFetchCache.current.inFlight = false
            setIsFetchingProfile(false)
          }
          return
        }

        // Retry with exponential backoff for network errors
        if (error.message?.includes('Failed to fetch') && retryAttempt < 3) {
          const delay = Math.pow(2, retryAttempt) * 1000 // 1s, 2s, 4s
          setTimeout(() => {
            fetchUserProfile(userId, userEmail, authUserFromSession, retryAttempt + 1)
          }, delay)
        }
        return
      }

      // If no profile exists, create a basic one using metadata from the session user
      if (!data) {
        try {
          // Use the provided user object from the session if available, otherwise fetch session (safer)
          let authUser = authUserFromSession
          if (!authUser) {
            const { data: { session } } = await supabase.auth.getSession()
            authUser = session?.user || null
          }

          if (!authUser) {
            console.warn('[AUTH CONTEXT] No auth user available to create profile metadata')
            return
          }

          let extractedName = null
          let extractedGender = null
          let extractedAge = null
          let extractedAvatar = null

          if (authUser) {
            const userMetadata = authUser.user_metadata
            const rawUserMetaData = (authUser as any).raw_user_meta_data

            // Extract name from metadata
            if (userMetadata?.full_name) {
              extractedName = userMetadata.full_name
            } else if (userMetadata?.name) {
              extractedName = userMetadata.name
            } else if (rawUserMetaData?.full_name) {
              extractedName = rawUserMetaData.full_name
            } else if (rawUserMetaData?.name) {
              extractedName = rawUserMetaData.name
            } else if (userEmail) {
              const emailName = userEmail.split('@')[0]
              extractedName = emailName
                .split(/[._-]/)
                .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                .join(' ')
            }

            // Extract gender
            if (userMetadata?.gender) {
              extractedGender = userMetadata.gender.toLowerCase()
            } else if (rawUserMetaData?.gender && typeof rawUserMetaData.gender === 'string') {
              extractedGender = rawUserMetaData.gender.toLowerCase()
            }

            // Extract age
            if (userMetadata?.age) {
              extractedAge = parseInt(userMetadata.age)
            } else if (rawUserMetaData?.age && typeof rawUserMetaData.age === 'string') {
              extractedAge = parseInt(rawUserMetaData.age)
            }

            // Extract avatar
            if (userMetadata?.avatar_url) {
              extractedAvatar = userMetadata.avatar_url
            } else if (userMetadata?.picture) {
              extractedAvatar = userMetadata.picture
            } else if (rawUserMetaData?.avatar_url && typeof rawUserMetaData.avatar_url === 'string') {
              extractedAvatar = rawUserMetaData.avatar_url
            } else if (rawUserMetaData?.picture && typeof rawUserMetaData.picture === 'string') {
              extractedAvatar = rawUserMetaData.picture
            }
          }

          // Determine age bracket
          let ageBracket = null
          if (extractedAge) {
            if (extractedAge < 30) {
              ageBracket = 'under_30'
            } else if (extractedAge >= 30 && extractedAge < 50) {
              ageBracket = '30_to_49'
            } else if (extractedAge >= 50) {
              ageBracket = '50_plus'
            }
          }

          // Map gender to enum
          let mappedGender = null
          if (extractedGender) {
            if (extractedGender === 'male' || extractedGender === 'm') {
              mappedGender = 'male'
            } else if (extractedGender === 'female' || extractedGender === 'f') {
              mappedGender = 'female'
            } else {
              mappedGender = 'unspecified'
            }
          }

          const affiliateCode = typeof window !== 'undefined' ? localStorage.getItem('sage_affiliate_ref') : null;

          const insertPayload: Database['public']['Tables']['users']['Insert'] = {
            id: userId as string,
            name: extractedName || undefined,
            email: userEmail || '',
            age: extractedAge ?? null,
            age_bracket: (ageBracket as Database['public']['Tables']['users']['Row']['age_bracket']) ?? null,
            gender: (mappedGender as Database['public']['Tables']['users']['Row']['gender']) ?? null,
            country_code: null,
            preferences: (extractedAvatar ? { avatar_url: extractedAvatar } : {}) as unknown as Database['public']['Tables']['users']['Insert']['preferences'],
            is_onboarded: true,
            subscription_status: 'free',
            affiliate_code_used: affiliateCode || undefined,
            affiliate_discount_applied: affiliateCode ? true : false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }

          const { data: newProfile, error: createError } = await supabase
            .from('users')
            .upsert(insertPayload as Database['public']['Tables']['users']['Insert'], { onConflict: 'id' })
            .select()
            .single()

          if (createError) {
            console.error('[AUTH CONTEXT] Error creating user profile:', createError)
            if (retryAttempt === 0) {
              profileFetchCache.current.inFlight = false
              setIsFetchingProfile(false)
            }
            return
          }

          if (newProfile) setUserProfile(toUserProfile(newProfile as Database['public']['Tables']['users']['Row']))
        } catch (err) {
          console.error('[AUTH CONTEXT] Unexpected error creating user profile:', err)
          if (retryAttempt === 0) {
            profileFetchCache.current.inFlight = false
            setIsFetchingProfile(false)
          }
          return
        }
      } else {
        if (data) setUserProfile(toUserProfile(data as Database['public']['Tables']['users']['Row']))
      }

      // Mark as fetched
      profileFetchCache.current.userId = userId
      profileFetchCache.current.lastFetchAt = Date.now()
    } catch (error: any) {
      if (error?.name === 'AbortError' || error?.message?.includes('AbortError')) {
        console.log('[AUTH CONTEXT] Profile fetch aborted (catch)')
      } else {
        console.error('[AUTH CONTEXT] Error fetching user profile:', error)
      }

      // Retry with exponential backoff for network errors
      if (error instanceof Error && error.message?.includes('Failed to fetch') && retryAttempt < 3) {
        const delay = Math.pow(2, retryAttempt) * 1000
        setTimeout(() => {
          fetchUserProfile(userId, userEmail, authUserFromSession, retryAttempt + 1)
        }, delay)
      }
    } finally {
      // Only reset the loading state if this specific request wasn't aborted
      // This prevents older aborted requests from resetting the state while a newer one is running
      if (retryAttempt === 0 && !signal.aborted) {
        profileFetchCache.current.inFlight = false
        setIsFetchingProfile(false)
      }
    }
  }, [supabase, toUserProfile])

  // Initialize auth state ONCE
  useEffect(() => {
    let mounted = true

    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!mounted) return

        const newUser = session?.user ?? null

        console.log('[AUTH CONTEXT] ✅ Initial session resolved:', {
          id: newUser?.id || 'none',
          email: newUser?.email,
          metadata: newUser?.user_metadata ? Object.keys(newUser.user_metadata) : []
        })

        // Cache the auth state
        cacheAuth(newUser)

        // Only update if changed
        if (newUser?.id !== user?.id) {
          setUser(newUser)

          if (newUser) {
            fetchUserProfile(newUser.id, newUser.email, newUser).catch(err => {
              console.error('[AUTH CONTEXT] Profile fetch failed:', err)
            })
          } else {
            setUserProfile(null)
          }
        }

        if (mounted) {
          setIsLoading(false)
          console.log('[AUTH CONTEXT] ✅ Auth initialization complete')
        }
      } catch (error) {
        console.error('[AUTH CONTEXT] Session fetch error:', error)
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    // Set up auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: any, session: any) => {
        if (!mounted) return

        console.log('[AUTH CONTEXT] 🔄 Auth state change:', event, session?.user?.id)

        const newUser = session?.user ?? null

        // Cache the auth state
        cacheAuth(newUser)

        setUser(newUser)

        if (newUser) {
          // Pass the session user object to avoid redundant network calls to /user
          fetchUserProfile(newUser.id, newUser.email, newUser).finally(() => {
            if (mounted) setIsLoading(false)
          })
        } else {
          setUserProfile(null)
          if (mounted) setIsLoading(false)
        }
      }
    )

    getInitialSession()

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty deps - only run once on mount (intentional)

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  }, [supabase])

  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    let displayName = name
    if (!displayName && email) {
      const emailName = email.split('@')[0]
      displayName = emailName
        .split(/[._-]/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ')
    }

    const additionalData: Record<string, unknown> = {
      name: displayName,
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/verify`,
        data: additionalData,
      },
    })

    return { data, error }
  }, [supabase])

  const signInWithGoogle = useCallback(async () => {
    // Store current path or redirect query param so AuthRedirectGuard routes back to the correct product
    try {
      const searchParams = new URLSearchParams(window.location.search)
      const redirectParam = searchParams.get('redirect') || searchParams.get('returnPath')
      const raw = redirectParam ? decodeURIComponent(redirectParam) : (window.location.pathname + window.location.search)
      // Guard: only store relative paths — reject absolute URLs and protocol-relative URLs
      const target = raw.startsWith('/') && !raw.startsWith('//') ? raw : window.location.pathname + window.location.search
      localStorage.setItem('sage_auth_return_path', target)
    } catch { }
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    })
    return { data, error }
  }, [supabase])

  const signInWithFacebook = useCallback(async () => {
    try {
      const searchParams = new URLSearchParams(window.location.search)
      const redirectParam = searchParams.get('redirect') || searchParams.get('returnPath')
      const raw = redirectParam ? decodeURIComponent(redirectParam) : (window.location.pathname + window.location.search)
      // Guard: only store relative paths — reject absolute URLs and protocol-relative URLs
      const target = raw.startsWith('/') && !raw.startsWith('//') ? raw : window.location.pathname + window.location.search
      localStorage.setItem('sage_auth_return_path', target)
    } catch { }
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: window.location.origin,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    })
    return { data, error }
  }, [supabase])

  const signOut = useCallback(async () => {
    console.log('[AUTH CONTEXT] Starting sign out...')

    // Clear local state IMMEDIATELY. This ensures the UI responds instantly
    // even if the Supabase network call hangs.
    setUser(null)
    setUserProfile(null)

    // Clear cache
    cacheAuth(null)
    profileFetchCache.current = { userId: null, lastFetchAt: 0, inFlight: false }
    setIsFetchingProfile(false)

    let signOutError = null
    try {
      // We wrap the Supabase call in a timeout race to prevent the UI from being 
      // stuck if the network is flaky or Supabase is slow to respond.
      const signOutPromise = supabase.auth.signOut()
      const timeoutPromise = new Promise<{ error: any }>((_, reject) =>
        setTimeout(() => reject(new Error('Sign out request timed out')), 5000)
      )

      const result = await Promise.race([signOutPromise, timeoutPromise]) as { error: any }
      if (result?.error) {
        signOutError = result.error
        console.warn('[AUTH CONTEXT] Supabase sign out returned an error:', result.error)
      } else {
        console.log('[AUTH CONTEXT] Supabase sign out call completed successfully')
      }
    } catch (err) {
      signOutError = err
      console.error('[AUTH CONTEXT] Sign out error or timeout:', err)
      // We continue anyway because local state is already cleared
    }

    console.log('[AUTH CONTEXT] Sign out process finished (local state cleared)')
    return { error: signOutError }
  }, [supabase])

  const updateProfile = useCallback(async (updates: Partial<Database['public']['Tables']['users']['Update']>) => {
    if (!user) return { error: new Error('No user logged in'), data: null }

    const { data, error } = await supabase
      .from('users')
      .update({
        ...(updates as Record<string, unknown>),
        ...(updates.preferences
          ? { preferences: updates.preferences as unknown as Database['public']['Tables']['users']['Row']['preferences'] }
          : {}),
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)
      .select()
      .single()

    if (!error && data) {
      setUserProfile(toUserProfile(data as Database['public']['Tables']['users']['Row']))
      // Force refresh
      setTimeout(() => {
        fetchUserProfile(user.id, user.email)
      }, 100)
    }

    return { data, error }
  }, [user, supabase, toUserProfile, fetchUserProfile])

  const resetPassword = useCallback(async (email: string) => {
    if (email.trim().toLowerCase().endsWith('@stillwaters.internal')) {
      return {
        data: null,
        error: new Error('Student accounts cannot reset passwords via email. Please ask your parent to reset your password from their dashboard.'),
      }
    }
        const { data, error } = await supabase.functions.invoke('org-signin-link', {
      body: {
        email,
        mode: 'recovery',
        redirect_to: `${window.location.origin}/auth/reset-password`,
      },
    })
    return { data, error }
  }, [supabase])


  const refreshUserProfile = useCallback(async () => {
    if (user) {
      // Clear cache to force a fresh fetch
      profileFetchCache.current = { userId: null, lastFetchAt: 0, inFlight: false }
      await fetchUserProfile(user.id, user.email)
    }
  }, [user, fetchUserProfile])

  const switchWorkspace = useCallback(async (facilityId: string, role: string) => {
    if (!user) return { error: new Error('No user logged in') }

    const { error } = await supabase
      .from('users')
      .update({
        facility_id: facilityId,
        facility_role: role,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)

    if (!error) {
      // Force refresh of the profile to immediately reflect new active workspace
      await refreshUserProfile()
    }

    return { error }
  }, [user, supabase, refreshUserProfile])

  const value: AuthContextType = {
    user,
    userProfile,
    isLoading,
    isFetchingProfile,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithFacebook,
    signOut,
    updateProfile,
    resetPassword,
    refreshUserProfile,
    switchWorkspace,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Alias for imported university components (their context exported both names)
export const useAuthContext = useAuth
